import { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Trash2 } from 'lucide-react';
import {
  UTILITIES, PARKING_TYPES, computeAllIn,
} from '../../lib/listingConstants';

const Toggle = ({ value, onChange, testId }) => (
  <div className="flex gap-2" data-testid={testId}>
    {[true, false].map((v) => (
      <button
        key={String(v)}
        type="button"
        onClick={() => onChange(v)}
        className={`flex-1 px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
          value === v ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-slate-200 bg-white text-slate-700'
        }`}
      >
        {v ? 'Yes' : 'No'}
      </button>
    ))}
  </div>
);

const NumberField = ({ label, value, onChange, placeholder, testId, disabled }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-slate-700">{label}</Label>
    <Input
      type="number"
      min="0"
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      placeholder={placeholder}
      className={`h-11 ${disabled ? 'bg-slate-50 text-slate-400' : ''}`}
      data-testid={testId}
    />
  </div>
);

const StepPricing = ({ listing, patchDraft, onNext, saving }) => {
  const [s, setS] = useState({
    monthly_rent_cad: listing.monthly_rent_cad ?? null,
    includes_utilities: listing.includes_utilities ?? null,
    utilities_included: listing.utilities_included || [],
    additional_utility_cost_cad: listing.additional_utility_cost_cad ?? null,
    includes_internet: listing.includes_internet ?? null,
    internet_cost_if_separate_cad: listing.internet_cost_if_separate_cad ?? null,
    includes_parking: listing.includes_parking ?? null,
    parking_cost_if_separate_cad: listing.parking_cost_if_separate_cad ?? null,
    parking_type: listing.parking_type || '',
    includes_laundry: listing.includes_laundry ?? null,
    laundry_cost_per_load_cad: listing.laundry_cost_per_load_cad ?? null,
    other_fees: listing.other_fees || [],
    security_deposit_cad: listing.security_deposit_cad ?? null,
    last_month_rent_required: listing.last_month_rent_required ?? null,
    key_deposit_cad: listing.key_deposit_cad ?? null,
  });
  const set = (patch) => setS((prev) => ({ ...prev, ...patch }));

  const toggleUtility = (u) => {
    setS((prev) => ({
      ...prev,
      utilities_included: prev.utilities_included.includes(u)
        ? prev.utilities_included.filter((x) => x !== u)
        : [...prev.utilities_included, u],
    }));
  };

  const addFee = () => set({ other_fees: [...s.other_fees, { name: '', amount_cad: 0, description: '' }] });
  const updateFee = (i, patch) => {
    const next = [...s.other_fees];
    next[i] = { ...next[i], ...patch };
    set({ other_fees: next });
  };
  const removeFee = (i) => set({ other_fees: s.other_fees.filter((_, j) => j !== i) });

  const total = useMemo(() => computeAllIn(s), [s]);

  // Each "cost if separate" must be filled (0 OK) when NOT included.
  const costsFilled =
    s.includes_utilities !== null &&
    s.includes_internet !== null &&
    s.includes_parking !== null &&
    (s.includes_utilities || s.additional_utility_cost_cad !== null) &&
    (s.includes_internet || s.internet_cost_if_separate_cad !== null) &&
    (s.includes_parking || s.parking_cost_if_separate_cad !== null);

  const canContinue =
    s.monthly_rent_cad !== null && s.monthly_rent_cad !== '' &&
    costsFilled &&
    s.security_deposit_cad !== null &&
    s.key_deposit_cad !== null &&
    s.last_month_rent_required !== null;

  const save = async () => {
    await patchDraft({
      ...s,
      monthly_rent_cad: Number(s.monthly_rent_cad),
    });
    onNext();
  };

  return (
    <div className="space-y-6" data-testid="pricing-step">
      <div>
        <h1 className="text-2xl font-heading font-extrabold text-slate-900">All-in pricing</h1>
        <p className="mt-1 text-sm text-slate-600">
          Every cost goes here. Students see <strong>one number</strong> — rent plus every extra.
        </p>
      </div>

      {/* Live preview */}
      <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200" data-testid="price-preview">
        <CardContent className="p-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
            Students will see
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-4xl font-heading font-extrabold text-slate-900" data-testid="live-total">
              ${total.toLocaleString()}
            </span>
            <span className="text-slate-600 text-sm">/month</span>
            <span className="ml-auto text-[10px] font-bold px-2 py-0.5 bg-amber-500 text-white rounded-full">
              ALL-IN
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-600">
            Rent {s.monthly_rent_cad ? `$${Number(s.monthly_rent_cad).toLocaleString()}` : '$0'}
            {!s.includes_utilities && s.additional_utility_cost_cad ? ` + utilities $${s.additional_utility_cost_cad}` : ''}
            {!s.includes_internet && s.internet_cost_if_separate_cad ? ` + internet $${s.internet_cost_if_separate_cad}` : ''}
            {!s.includes_parking && s.parking_cost_if_separate_cad ? ` + parking $${s.parking_cost_if_separate_cad}` : ''}
            {s.other_fees.length > 0
              ? ` + ${s.other_fees.length} fee${s.other_fees.length > 1 ? 's' : ''}`
              : ''}
          </p>
        </CardContent>
      </Card>

      {/* Rent */}
      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Monthly rent (CAD)</Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
          <Input
            type="number"
            min="0"
            value={s.monthly_rent_cad ?? ''}
            onChange={(e) => set({ monthly_rent_cad: e.target.value === '' ? null : Number(e.target.value) })}
            className="h-12 pl-8"
            placeholder="1200"
            data-testid="rent-input"
          />
        </div>
      </div>

      {/* Utilities */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-slate-900">Are utilities included?</Label>
        <Toggle
          value={s.includes_utilities}
          onChange={(v) => set({ includes_utilities: v })}
          testId="utilities-toggle"
        />
        {s.includes_utilities === true && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="utilities-list">
            {UTILITIES.map((u) => (
              <button
                key={u.value}
                type="button"
                onClick={() => toggleUtility(u.value)}
                className={`px-3 py-1.5 rounded-full border text-xs font-medium ${
                  s.utilities_included.includes(u.value)
                    ? 'border-amber-500 bg-amber-50 text-amber-900'
                    : 'border-slate-200 bg-white text-slate-700'
                }`}
                data-testid={`utility-${u.value}`}
              >
                {u.label}
              </button>
            ))}
          </div>
        )}
        {s.includes_utilities === false && (
          <NumberField
            label="Average monthly utility bill"
            value={s.additional_utility_cost_cad}
            onChange={(v) => set({ additional_utility_cost_cad: v })}
            placeholder="150"
            testId="utility-cost"
          />
        )}
      </div>

      {/* Internet */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-slate-900">Is internet included?</Label>
        <Toggle
          value={s.includes_internet}
          onChange={(v) => set({ includes_internet: v })}
          testId="internet-toggle"
        />
        {s.includes_internet === false && (
          <NumberField
            label="Monthly internet cost"
            value={s.internet_cost_if_separate_cad}
            onChange={(v) => set({ internet_cost_if_separate_cad: v })}
            placeholder="60"
            testId="internet-cost"
          />
        )}
      </div>

      {/* Parking */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-slate-900">Is parking included?</Label>
        <Toggle
          value={s.includes_parking}
          onChange={(v) => set({ includes_parking: v })}
          testId="parking-toggle"
        />
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">Parking type</Label>
          <div className="grid grid-cols-4 gap-1.5">
            {PARKING_TYPES.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => set({ parking_type: p.value })}
                className={`px-2 py-2 rounded-lg border text-xs ${
                  s.parking_type === p.value ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white'
                }`}
                data-testid={`parking-type-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {s.includes_parking === false && s.parking_type !== 'none' && (
          <NumberField
            label="Monthly parking cost"
            value={s.parking_cost_if_separate_cad}
            onChange={(v) => set({ parking_cost_if_separate_cad: v })}
            placeholder="80"
            testId="parking-cost"
          />
        )}
      </div>

      {/* Laundry */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-slate-900">Is laundry included?</Label>
        <Toggle
          value={s.includes_laundry}
          onChange={(v) => set({ includes_laundry: v })}
          testId="laundry-toggle"
        />
        {s.includes_laundry === false && (
          <NumberField
            label="Cost per load (if coin/card)"
            value={s.laundry_cost_per_load_cad}
            onChange={(v) => set({ laundry_cost_per_load_cad: v })}
            placeholder="3"
            testId="laundry-cost"
          />
        )}
      </div>

      {/* Other fees */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-slate-900">Other recurring fees</Label>
          <button
            type="button"
            onClick={addFee}
            className="inline-flex items-center text-xs font-medium text-amber-700 hover:text-amber-900"
            data-testid="add-fee-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add fee
          </button>
        </div>
        {s.other_fees.map((f, i) => (
          <Card key={i} className="border-slate-200" data-testid={`other-fee-${i}`}>
            <CardContent className="p-3 space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Fee name (e.g., Cleaning)"
                  value={f.name}
                  onChange={(e) => updateFee(i, { name: e.target.value })}
                  className="flex-1 h-10"
                  maxLength={60}
                />
                <div className="relative w-32">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={f.amount_cad ?? ''}
                    onChange={(e) => updateFee(i, { amount_cad: Number(e.target.value || 0) })}
                    className="h-10 pl-8"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeFee(i)}
                  className="h-10 w-10 flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded-lg"
                  data-testid={`remove-fee-${i}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <Input
                placeholder="Description (optional)"
                value={f.description || ''}
                onChange={(e) => updateFee(i, { description: e.target.value })}
                className="h-9 text-xs"
                maxLength={200}
              />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Deposits */}
      <div className="grid grid-cols-2 gap-3">
        <NumberField
          label="Security deposit"
          value={s.security_deposit_cad}
          onChange={(v) => set({ security_deposit_cad: v })}
          placeholder="0"
          testId="security-deposit"
        />
        <NumberField
          label="Key deposit"
          value={s.key_deposit_cad}
          onChange={(v) => set({ key_deposit_cad: v })}
          placeholder="0"
          testId="key-deposit"
        />
      </div>
      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Last month's rent required?</Label>
        <Toggle
          value={s.last_month_rent_required}
          onChange={(v) => set({ last_month_rent_required: v })}
          testId="last-month-rent-toggle"
        />
      </div>

      <Button
        className="w-full btn-primary h-12 text-base"
        disabled={!canContinue || saving}
        onClick={save}
        data-testid="step-pricing-continue"
      >
        Continue
      </Button>
    </div>
  );
};

export default StepPricing;
